import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Icon, MenuItem, TextField } from '@mui/material'
import { isElement, orderBy } from 'lodash'

const Select = ({
    label = '',
    value = '',
    items = [],
    onChange = () => { },
    disabled = false,
    sortItems = true,
    sortKey = 'label',
    undefinedValue = false,
    nullLabel = '',
    multiple = false,
    required,
}) => {
    const defaultValue = value ?? (multiple ? [] : nullLabel)

    const sortedItems = useMemo(() => {
        if (!sortItems) {
            return items
        }
        return orderBy(items, sortKey)
    }, [items, sortItems, sortKey])

    const formattedItems = useMemo(() => {
        const formatted = sortedItems.map(item => ({
            value: item.id ?? item.code ?? item.value,
            label: item.name || item.label || item.title || ' ',
            icon: item.icon,
        }))
        if (!undefinedValue) {
            return formatted
        }
        return [
            { value: undefined, label: nullLabel },
            ...formatted,
        ]
    }, [sortedItems, undefinedValue, nullLabel])

    return (
        <TextField
            label={label}
            value={defaultValue}
            onChange={e => {
                if (multiple && e.target.value.includes(undefined)) {
                    onChange([])
                    return
                }
                onChange(e.target.value)
            }}
            select
            disabled={disabled}
            slotProps={{
                select: {
                    multiple,
                },
            }}
            required={required}
        >
            {formattedItems.map((obj, i) => (
                <MenuItem key={i} value={obj.value}>
                    {obj.icon && !isElement(obj.icon) && <Icon sx={{ marginRight: '5px' }}>{obj.icon}</Icon>}
                    {obj.icon && isElement(obj.icon) && obj.icon}
                    {obj.label}
                </MenuItem>
            ))}
        </TextField>
    )
}

Select.propTypes = {
    label: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.node,
    ]),
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    ]),
    items: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        icon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
        // + sortKey
        // + name, title, id, code
    })),
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    sortItems: PropTypes.bool,
    sortKey: PropTypes.string,
    undefinedValue: PropTypes.bool,
    multiple: PropTypes.bool,
    required: PropTypes.bool,
    nullLabel: PropTypes.string,
}

export default Select